import React, { useState } from 'react';
import { Modal, Button, Alert, DatePicker, Select } from 'antd';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import FilterDropdown from "../filters/FilterDropdown";
import {formatOrderStatus, ORDER_STATUSES} from "../../utils/formatOrderStatus";
import styles from '../../styles/exportOrdersModal.module.scss';

const { RangePicker } = DatePicker;

const ExportOrdersModal = ({ isOpen, onClose, onExport }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        dateRange: null,
        status: undefined,
    });

    const handleDateChange = (dates) => {
        setFilters((prev) => ({
            ...prev,
            dateRange: dates,
        }));
    };

    const handleStatusChange = (filter) => {
        setFilters((prev) => ({
            ...prev,
            status: filter.status,
        }));
    };

    const statusSections = [
        {
            type: 'status',
            title: 'Status porudžbine',
            items: ORDER_STATUSES,
        },
    ];

    const handleExport = async () => {
        setLoading(true);
        setError('');
        toast.loading('Izvoz porudžbina...');

        try {
            const exportParams = {
                createdFrom: filters.dateRange?.[0]
                    ? filters.dateRange[0].format('YYYY-MM-DD')
                    : undefined,
                createdTo: filters.dateRange?.[1]
                    ? filters.dateRange[1].format('YYYY-MM-DD')
                    : undefined,
                status: filters.status || undefined,
            };

            const result = await onExport(exportParams);

            if (!result.success) {
                setError(result.message);
                toast.dismiss();
                toast.error(result.message || 'Došlo je do greške pri izvozu.');
                setLoading(false);
                return;
            }

            toast.dismiss();

            if (result.data === null) {
                toast.info(result.message || 'Nema porudžbina za izvoz.');
            } else {
                toast.success('Porudžbine uspešno izvezene.');
            }

            setFilters({
                dateRange: null,
                status: null,
            });
            setError('');
            setLoading(false);
            onClose();
        } catch (error) {
            console.error('Error exporting orders:', error);
            setError(error.message || 'Došlo je do greške pri izvozu porudžbina.');
            toast.dismiss();
            toast.error('Došlo je do greške pri izvozu.');
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFilters({
            dateRange: null,
            status: undefined,
        });
        setError('');
        onClose();
    };

    return (
        <Modal
            className={styles.export_orders_modal}
            title="Izvezi porudžbine"
            open={isOpen}
            onCancel={handleCancel}
            footer={[
                <Button
                    key="cancel"
                    onClick={handleCancel}
                    className="btn-secondary"
                    disabled={loading}
                >
                    Otkaži
                </Button>,
                <Button
                    key="export"
                    type="primary"
                    onClick={handleExport}
                    className="btn-primary"
                    disabled={loading}
                    icon={
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 12L3 7h3V1h4v6h3L8 12z"/>
                            <path d="M1 14h14v2H1z"/>
                        </svg>
                    }
                >
                    {loading ? 'Izvoz...' : 'Izvezi'}
                </Button>,
            ]}
        >
            <div className={styles.export_orders_modal_form}>
                <div>
                    <label>Period (opciono)</label>
                    <RangePicker
                        placeholder={['Datum od', 'Datum do']}
                        format="DD.MM.YYYY"
                        value={filters.dateRange}
                        onChange={handleDateChange}
                        allowClear
                        allowEmpty={[true, true]}
                    />
                </div>

                <div>
                    <label>Status porudžbine (opciono)</label>
                    <FilterDropdown
                        sections={statusSections}
                        selectedFilter={{ status: filters.status }}
                        onFilterChange={handleStatusChange}
                        buttonLabel={formatOrderStatus(filters.status)}
                    />
                </div>

                {error && (
                    <Alert
                        message={error}
                        type="error"
                        showIcon
                    />
                )}
            </div>
        </Modal>
    );
};

ExportOrdersModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onExport: PropTypes.func.isRequired,
};

export default ExportOrdersModal;